import { legalDocumentsApi } from '../api/legalDocumentsApi';
import { getAdminApiError } from '../lib/adminApiErrors';
import { getAdminResponseStatus, mustClearSensitiveAdminState } from '../lib/legalAdminErrors';
import type { AdminDocumentPackageBundle } from '../legalTypes';
import {
  clearSensitiveDocumentState,
  type DocumentActions,
  type DocumentRuntime,
  type SetAdminStore,
} from './legalDocumentStoreRuntime';

const emptyBundle = (packageId: string): AdminDocumentPackageBundle => ({
  manifest: { packageId, documents: [] },
  versions: { packageId, documents: [] },
  job: null,
  timeline: { packageId, events: [] },
  evidence: null,
});

const describePartialFailures = (
  count: number,
  evidenceUnavailable: boolean
): string | null => {
  if (count === 0) {
    return null;
  }
  if (evidenceUnavailable) {
    const otherCount = count - 1;
    const suffix = otherCount > 0
      ? ` ${otherCount} additional detail section${otherCount === 1 ? '' : 's'} could not be loaded.`
      : '';
    return `Stored signature evidence could not be loaded; execution remains disabled.${suffix}`;
  }
  return `${count} document detail section${count === 1 ? '' : 's'} could not be loaded.`;
};

type PackageListActions = Pick<
  DocumentActions,
  'fetchDocumentPackages' | 'resolveDocumentPackageCompany'
>;

const createPackageListActions = (
  set: SetAdminStore,
  runtime: DocumentRuntime
): PackageListActions => {
  const fetchDocumentPackages: PackageListActions['fetchDocumentPackages'] = async (query) => {
    runtime.listController?.abort();
    const controller = new AbortController();
    runtime.listController = controller;
    set({ documentListLoading: true, documentError: null });
    try {
      const page = await legalDocumentsApi.searchPackages(query, controller.signal);
      if (runtime.listController !== controller) {
        return page;
      }
      set({ documentPackages: page.items, documentPackagePage: page, documentListLoading: false });
      return page;
    } catch (error) {
      if (runtime.listController !== controller) {
        throw error;
      }
      if (mustClearSensitiveAdminState(error)) {
        clearSensitiveDocumentState(set, runtime);
      }
      set({
        documentListLoading: false,
        documentError: getAdminApiError(error, 'document_packages_failed'),
      });
      throw error;
    }
  };
  const resolveDocumentPackageCompany: PackageListActions['resolveDocumentPackageCompany'] = async (
    packageId
  ) => {
    try {
      const result = await legalDocumentsApi.searchPackages({
        q: packageId,
        page: 1,
        pageSize: 100,
      });
      return result.items.find((item) => item.id === packageId)?.companyId ?? null;
    } catch (error) {
      if (mustClearSensitiveAdminState(error)) {
        clearSensitiveDocumentState(set, runtime);
      }
      set({ documentError: getAdminApiError(error, 'document_package_resolution_failed') });
      throw error;
    }
  };
  return { fetchDocumentPackages, resolveDocumentPackageCompany };
};

type WorkspaceActions = Pick<
  DocumentActions,
  'loadDocumentWorkspace' | 'clearDocumentWorkspace'
>;

const createDocumentWorkspaceActions = (
  set: SetAdminStore,
  runtime: DocumentRuntime
): WorkspaceActions => {
  const loadDocumentWorkspace: WorkspaceActions['loadDocumentWorkspace'] = async (
    companyId,
    packageId
  ) => {
    const generation = ++runtime.detailGeneration;
    set({
      documentPackage: null,
      documentBundle: null,
      legalProfile: null,
      legalProfileConflict: null,
      documentDetailLoading: true,
      documentPartialError: null,
      documentError: null,
      documentConflict: null,
    });
    try {
      const [detail, profile] = await Promise.all([
        legalDocumentsApi.getPackage(companyId, packageId),
        legalDocumentsApi.getLegalProfile(companyId),
      ]);
      const optional = await Promise.allSettled([
        legalDocumentsApi.getManifest(companyId, packageId),
        legalDocumentsApi.getVersions(companyId, packageId),
        legalDocumentsApi.getJobStatus(companyId, packageId),
        legalDocumentsApi.getTimeline(companyId, packageId),
        legalDocumentsApi.getSignatureEvidence(companyId, packageId),
      ]);
      if (generation !== runtime.detailGeneration) {
        return;
      }
      const sensitiveFailure = optional.find((result) =>
        result.status === 'rejected' && mustClearSensitiveAdminState(result.reason));
      if (sensitiveFailure?.status === 'rejected') {
        throw sensitiveFailure.reason;
      }
      const fallback = emptyBundle(packageId);
      const bundle: AdminDocumentPackageBundle = {
        manifest: optional[0].status === 'fulfilled' ? optional[0].value : fallback.manifest,
        versions: optional[1].status === 'fulfilled' ? optional[1].value : fallback.versions,
        job: optional[2].status === 'fulfilled' ? optional[2].value : null,
        timeline: optional[3].status === 'fulfilled' ? optional[3].value : fallback.timeline,
        evidence: optional[4].status === 'fulfilled' ? optional[4].value : null,
      };
      const partialFailures = optional.filter((result) => result.status === 'rejected').length;
      const evidenceUnavailable = optional[4].status === 'rejected';
      set({
        documentPackage: detail,
        legalProfile: profile,
        documentBundle: bundle,
        documentDetailLoading: false,
        documentPartialError: describePartialFailures(partialFailures, evidenceUnavailable),
      });
    } catch (error) {
      if (generation === runtime.detailGeneration) {
        if (mustClearSensitiveAdminState(error)) {
          clearSensitiveDocumentState(set, runtime);
        }
        set({
          documentPackage: null,
          documentBundle: null,
          legalProfile: null,
          documentDetailLoading: false,
          documentError: getAdminApiError(error, 'document_package_detail_failed'),
        });
      }
      throw error;
    }
  };

  const clearDocumentWorkspace = () => {
    runtime.detailGeneration += 1;
    runtime.mutationIdentity = null;
    set({
      documentPackage: null,
      documentBundle: null,
      legalProfile: null,
      legalProfileConflict: null,
      documentDetailLoading: false,
      documentMutationLoading: false,
      documentPartialError: null,
      documentConflict: null,
    });
  };
  return { loadDocumentWorkspace, clearDocumentWorkspace };
};

type CompanySummaryActions = Pick<
  DocumentActions,
  'loadCompanyLegalSummary' | 'clearCompanyLegalSummary'
>;

const createCompanySummaryActions = (
  set: SetAdminStore,
  runtime: DocumentRuntime
): CompanySummaryActions => {
  const loadCompanyLegalSummary: CompanySummaryActions['loadCompanyLegalSummary'] = async (companyId) => {
    const generation = ++runtime.summaryGeneration;
    set({
      companyLegalSummaryLoading: true,
      companyLegalSummary: null,
      companyLegalSummaryError: null,
    });
    try {
      const [profile, packages] = await Promise.all([
        legalDocumentsApi.getLegalProfile(companyId).catch((error: unknown) => {
          // A company can legitimately have no legal profile until its first document workflow.
          if (getAdminResponseStatus(error) === 404) {
            return null;
          }
          throw error;
        }),
        legalDocumentsApi.searchPackages({ companyId, page: 1, pageSize: 25 }),
      ]);
      if (generation !== runtime.summaryGeneration) {
        return;
      }
      set({
        companyLegalSummary: {
          companyId,
          profile,
          packages: packages.items,
          retainedEvidence: packages.items.length > 0,
        },
        companyLegalSummaryLoading: false,
      });
    } catch (error) {
      if (generation !== runtime.summaryGeneration) {
        throw error;
      }
      if (mustClearSensitiveAdminState(error)) {
        clearSensitiveDocumentState(set, runtime);
      }
      set({
        companyLegalSummaryLoading: false,
        companyLegalSummaryError: getAdminApiError(error, 'company_legal_summary_failed'),
      });
      throw error;
    }
  };

  const clearCompanyLegalSummary = () => {
    runtime.summaryGeneration += 1;
    set({
      companyLegalSummary: null,
      companyLegalSummaryLoading: false,
      companyLegalSummaryError: null,
    });
  };
  return { loadCompanyLegalSummary, clearCompanyLegalSummary };
};

export const createLegalDocumentReadActions = (
  set: SetAdminStore,
  runtime: DocumentRuntime
): Pick<
  DocumentActions,
  | 'fetchDocumentPackages'
  | 'resolveDocumentPackageCompany'
  | 'loadDocumentWorkspace'
  | 'clearDocumentWorkspace'
  | 'loadCompanyLegalSummary'
  | 'clearCompanyLegalSummary'
> => ({
  ...createPackageListActions(set, runtime),
  ...createDocumentWorkspaceActions(set, runtime),
  ...createCompanySummaryActions(set, runtime),
});
